import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RefreshCw, Sparkles, CheckCircle2, Layers } from 'lucide-react'
import {
  useContentTypes,
  useContentSummary,
  useContentTargets,
  useGenerateContent,
  useEnqueueContent,
} from '../hooks/useContentCoverage'
import type { ContentType, RecommendationProduct } from '../api/contentApi'

type Filter = 'all' | 'missing' | 'covered'

export function ContentCoveragePage() {
  const { data: types } = useContentTypes()
  const [type, setType] = useState<ContentType>('artwork_editorial')
  const [filter, setFilter] = useState<Filter>('missing')

  const { data: summary, isLoading: sumLoading, refetch: refetchSum } = useContentSummary(type)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useContentTargets(type, filter)
  const generate = useGenerateContent()
  const enqueue = useEnqueueContent()

  const items: RecommendationProduct[] = (data?.pages ?? []).flatMap((p) => p.items)
  const total = summary?.totalTargets ?? null
  const coveredOnPage = items.filter((i) => i.covered).length
  const missingOnPage = items.filter((i) => !i.covered).map((i) => i.handle)
  const activeType = types?.find((t) => t.id === type)

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-600" /> Content Coverage</h1>
          <p className="text-sm text-muted-foreground">Generate and publish per-artwork / per-artist metadata across the catalog (content-curation agent).</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { refetchSum(); refetch() }}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Content-type selector + stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Layers className="h-4 w-4" /> Content type</div>
            <Select value={type} onValueChange={(v) => setType(v as ContentType)}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {(types ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <StatCard icon={<Layers className="h-4 w-4 text-muted-foreground" />} label={activeType?.kind === 'collection' ? 'Artist collections' : 'Catalog targets'} value={sumLoading ? null : (total != null ? total.toLocaleString() : '—')} />
        <StatCard icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />} label="Covered (this view)" value={isLoading ? null : coveredOnPage.toLocaleString()} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Targets</CardTitle>
            <CardDescription>Generate runs synchronously; “Enqueue missing” fans the page into the background queue.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <TabsList>
                <TabsTrigger value="missing">Missing</TabsTrigger>
                <TabsTrigger value="covered">Covered</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              size="sm"
              disabled={!missingOnPage.length || enqueue.isPending}
              onClick={() => enqueue.mutate({ type, handles: missingOnPage })}
            >
              Enqueue missing ({missingOnPage.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No targets in this view.</p>
          ) : (
            <div className="divide-y">
              {items.map((p) => (
                <div key={p.handle} className="flex items-center gap-3 py-2.5">
                  <div className="h-12 w-10 rounded bg-muted overflow-hidden shrink-0">
                    {p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.handle}</p>
                  </div>
                  {p.covered
                    ? <Badge variant="secondary" className="text-emerald-700 bg-emerald-50">Covered</Badge>
                    : <Badge variant="outline" className="text-amber-700">Missing</Badge>}
                  <Button
                    size="sm"
                    variant={p.covered ? 'outline' : 'default'}
                    disabled={generate.isPending}
                    onClick={() => generate.mutate({ type, target_ref: p.handle })}
                  >
                    {generate.isPending && generate.variables?.target_ref === p.handle ? 'Generating…' : (p.covered ? 'Regenerate' : 'Generate')}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {hasNextPage && (
            <div className="pt-4 text-center">
              <Button variant="ghost" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">{icon}{label}</div>
        {value == null ? <Skeleton className="h-7 w-20" /> : <p className="text-2xl font-semibold">{value}</p>}
      </CardContent>
    </Card>
  )
}
